import type { LifeDimensions } from '../domain/life-state'
import { mapLifeAppearance, type ParticleAppearance } from './particle-appearance'
import type { LifeRenderer, LifeRendererInput } from './life-renderer'
import {
  createSimulationState,
  stepSimulation,
  type ParticleSimulationState,
} from './particle-simulation'
import {
  createParticleTopology,
  type ParticleTopology,
} from './particle-topology'

const DEFAULT_DIMENSIONS: LifeDimensions = {
  energy: 18,
  curiosity: 12,
  connection: 8,
  memory: 0,
  balance: 50,
}

const APPEARANCE_TRANSITION_MS = 1_200
const DESKTOP_NODE_COUNT = 120
const MOBILE_NODE_COUNT = 84
const MAX_STRAND_COUNT = 14

const mix = (from: number, to: number, progress: number): number =>
  from + (to - from) * progress

function interpolateAppearance(
  from: ParticleAppearance,
  to: ParticleAppearance,
  progress: number,
): ParticleAppearance {
  const boundedProgress = Math.min(1, Math.max(0, progress))
  return {
    glow: mix(from.glow, to.glow, boundedProgress),
    breathAmplitude: mix(from.breathAmplitude, to.breathAmplitude, boundedProgress),
    strandCount: Math.round(mix(from.strandCount, to.strandCount, boundedProgress)),
    strandReach: mix(from.strandReach, to.strandReach, boundedProgress),
    edgeOpacity: mix(from.edgeOpacity, to.edgeOpacity, boundedProgress),
    persistentSporeCount: Math.round(mix(
      from.persistentSporeCount,
      to.persistentSporeCount,
      boundedProgress,
    )),
    irregularity: mix(from.irregularity, to.irregularity, boundedProgress),
  }
}

export class ParticleMyceliumRenderer implements LifeRenderer {
  private readonly context: CanvasRenderingContext2D
  private readonly topology: ParticleTopology
  private readonly simulation: ParticleSimulationState
  private readonly resizeObserver: ResizeObserver | null
  private readonly onWindowResize = () => this.resize()
  private appearance = mapLifeAppearance(DEFAULT_DIMENSIONS)
  private appearanceFrom = this.appearance
  private appearanceTarget = this.appearance
  private appearanceChangedAt = 0
  private input: LifeRendererInput = {
    x: 0,
    y: 0,
    force: 0,
    speed: 0,
    active: false,
  }
  private width = 1
  private height = 1
  private radius = 1
  private running = false
  private disposed = false
  private frameHandle: number | null = null
  private lastFrameAt = 0

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly options: {
      seed: string
      reducedMotion?: () => boolean
    },
  ) {
    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error('canvas_2d_unavailable')
    }
    this.context = context
    this.canvas.dataset.reaction = 'idle'
    const mobile = this.isMobile()
    this.topology = createParticleTopology(
      options.seed,
      mobile ? MOBILE_NODE_COUNT : DESKTOP_NODE_COUNT,
      MAX_STRAND_COUNT,
    )
    this.simulation = createSimulationState(this.topology)
    this.resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(() => this.resize())
    if (this.resizeObserver) {
      this.resizeObserver.observe(canvas)
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onWindowResize)
    }
    this.resize()
    this.draw(0)
  }

  setState(dimensions: LifeDimensions): void {
    this.appearanceFrom = this.appearance
    this.appearanceTarget = mapLifeAppearance(dimensions)
    this.appearanceChangedAt = typeof performance === 'undefined' ? 0 : performance.now()
  }

  setInput(input: LifeRendererInput): void {
    this.input = {
      x: input.x,
      y: input.y,
      force: input.force,
      speed: input.speed ?? 0,
      active: input.active ?? input.force > 0,
    }
    const pointerDistance = Math.hypot(this.input.x, this.input.y)
    this.canvas.dataset.reaction = !this.input.active
      ? 'idle'
      : (this.input.speed ?? 0) >= 0.55
        ? 'contracted'
        : (this.input.speed ?? 0) <= 0.08 && pointerDistance < 0.6
          ? 'gentle'
          : 'idle'
  }

  start(): void {
    if (this.running || this.disposed) {
      return
    }
    this.running = true
    if (this.options.reducedMotion?.()) {
      this.draw(typeof performance === 'undefined' ? 0 : performance.now())
      this.running = false
      return
    }
    this.scheduleFrame()
  }

  stop(): void {
    if (!this.running && this.frameHandle === null) {
      return
    }
    this.running = false
    if (this.frameHandle !== null) {
      cancelAnimationFrame(this.frameHandle)
      this.frameHandle = null
    }
  }

  dispose(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true
    this.stop()
    this.resizeObserver?.disconnect()
    if (!this.resizeObserver && typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onWindowResize)
    }
    this.context.clearRect(0, 0, this.width, this.height)
    delete this.canvas.dataset.reaction
    this.canvas.style.removeProperty('--life-visible-diameter')
  }

  private readonly tick = (time: number) => {
    this.frameHandle = null
    if (!this.running || this.disposed) {
      return
    }
    this.draw(time)
    this.scheduleFrame()
  }

  private scheduleFrame(): void {
    if (!this.running || this.frameHandle !== null) {
      return
    }
    this.frameHandle = requestAnimationFrame(this.tick)
  }

  private resize(): void {
    const rect = this.canvas.getBoundingClientRect()
    this.width = Math.max(1, Math.round(rect.width || this.canvas.clientWidth || 1))
    this.height = Math.max(1, Math.round(rect.height || this.canvas.clientHeight || 1))
    const mobile = this.isMobile()
    const pixelRatio = Math.min(
      mobile ? 1.5 : 2,
      Math.max(1, typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1),
    )
    this.canvas.width = Math.round(this.width * pixelRatio)
    this.canvas.height = Math.round(this.height * pixelRatio)
    this.context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    this.radius = Math.min(this.width, this.height) * (mobile ? 0.085 : 0.075)
    this.canvas.style.setProperty('--life-visible-diameter', `${this.radius * 2}px`)
  }

  private draw(time: number): void {
    const deltaMs = this.lastFrameAt === 0 ? 16 : Math.min(50, Math.max(0, time - this.lastFrameAt))
    this.lastFrameAt = time
    const transitionProgress = this.appearanceChangedAt === 0
      ? 1
      : (time - this.appearanceChangedAt) / APPEARANCE_TRANSITION_MS
    this.appearance = interpolateAppearance(
      this.appearanceFrom,
      this.appearanceTarget,
      transitionProgress,
    )
    stepSimulation(this.simulation, this.topology, {
      x: this.input.x,
      y: this.input.y,
      force: this.input.force,
      speed: this.input.speed ?? 0,
      active: this.input.active ?? false,
    }, deltaMs, time)

    const centerX = this.width / 2 + this.simulation.offsetX * this.radius
    const centerY = this.height / 2 + this.simulation.offsetY * this.radius
    this.context.clearRect(0, 0, this.width, this.height)
    this.drawHalo(centerX, centerY)
    this.drawStrands(centerX, centerY, time)
    this.drawEdges(centerX, centerY, time)
    this.drawPersistentSpores(centerX, centerY, time)
    this.drawTransientSpores(centerX, centerY)
    this.drawNodes(centerX, centerY, time)
    this.drawCore(centerX, centerY)
  }

  private drawHalo(centerX: number, centerY: number): void {
    const gradient = this.context.createRadialGradient(
      centerX,
      centerY,
      0,
      centerX,
      centerY,
      this.radius * 3.2,
    )
    gradient.addColorStop(0, `rgba(131, 211, 193, ${0.025 + this.appearance.glow * 0.045})`)
    gradient.addColorStop(0.35, 'rgba(74, 125, 114, 0.01)')
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)')
    this.context.fillStyle = gradient
    this.context.fillRect(
      centerX - this.radius * 3.2,
      centerY - this.radius * 3.2,
      this.radius * 6.4,
      this.radius * 6.4,
    )
  }

  private drawStrands(centerX: number, centerY: number, time: number): void {
    this.context.lineCap = 'round'
    const count = Math.min(this.appearance.strandCount, this.topology.strands.length)
    for (const strand of this.topology.strands.slice(0, count)) {
      const position = this.screenPosition(strand.origin, centerX, centerY, time)
      if (!position) {
        continue
      }
      const startX = position.x
      const startY = position.y
      const pointerAngle = Math.atan2(this.input.y, this.input.x)
      const pointerInfluence = this.input.active ? 0.14 : 0
      const sway = Math.sin(time * 0.00072 + strand.phase) * strand.bend
      const angle = strand.angle + sway + pointerAngle * pointerInfluence
      const length = strand.length * this.radius * this.appearance.strandReach
      const endX = startX + Math.cos(angle) * length
      const endY = startY + Math.sin(angle) * length
      const controlX = startX + Math.cos(angle + strand.bend) * length * 0.52
      const controlY = startY + Math.sin(angle + strand.bend) * length * 0.52
      const gradient = this.context.createLinearGradient(startX, startY, endX, endY)
      gradient.addColorStop(0, `rgba(158, 224, 210, ${0.12 + this.appearance.glow * 0.13})`)
      gradient.addColorStop(0.7, 'rgba(115, 187, 172, 0.04)')
      gradient.addColorStop(1, 'rgba(90, 160, 146, 0)')
      this.context.strokeStyle = gradient
      this.context.lineWidth = 0.55
      this.context.beginPath()
      this.context.moveTo(startX, startY)
      this.context.quadraticCurveTo(controlX, controlY, endX, endY)
      this.context.stroke()
    }
  }

  private drawEdges(centerX: number, centerY: number, time: number): void {
    this.context.lineWidth = 0.45
    for (const edge of this.topology.edges) {
      const from = this.screenPosition(edge.from, centerX, centerY, time)
      const to = this.screenPosition(edge.to, centerX, centerY, time)
      if (!from || !to) {
        continue
      }
      this.context.strokeStyle = `rgba(145, 207, 194, ${this.appearance.edgeOpacity})`
      this.context.beginPath()
      this.context.moveTo(from.x, from.y)
      this.context.lineTo(to.x, to.y)
      this.context.stroke()
    }
  }

  private drawPersistentSpores(centerX: number, centerY: number, time: number): void {
    for (let index = 0; index < this.appearance.persistentSporeCount; index += 1) {
      const angle = index * 2.399963 + time * 0.000025 * (index % 2 === 0 ? 1 : -1)
      const distance = this.radius * (1.3 + (index % 5) * 0.18)
      this.drawPoint(
        centerX + Math.cos(angle) * distance,
        centerY + Math.sin(angle) * distance * 0.72,
        0.6,
        0.18,
      )
    }
  }

  private drawTransientSpores(centerX: number, centerY: number): void {
    for (const spore of this.simulation.spores) {
      const opacity = Math.max(0, 1 - spore.ageMs / spore.lifeMs) * 0.5
      this.drawPoint(
        centerX + spore.x * this.radius,
        centerY + spore.y * this.radius,
        0.8,
        opacity,
      )
    }
  }

  private drawNodes(centerX: number, centerY: number, time: number): void {
    this.context.shadowColor = 'rgba(139, 231, 210, 0.45)'
    this.context.shadowBlur = 4 + this.appearance.glow * 6
    for (let index = 0; index < this.simulation.nodes.length; index += 1) {
      const position = this.screenPosition(index, centerX, centerY, time)
      const source = this.topology.nodes[index]
      if (!position || !source) {
        continue
      }
      this.drawPoint(
        position.x,
        position.y,
        source.size,
        source.alpha * (0.55 + this.appearance.glow * 0.45),
      )
    }
    this.context.shadowBlur = 0
  }

  private drawCore(centerX: number, centerY: number): void {
    const gradient = this.context.createRadialGradient(
      centerX,
      centerY,
      0,
      centerX,
      centerY,
      this.radius * 0.27,
    )
    gradient.addColorStop(0, `rgba(228, 249, 244, ${0.16 + this.appearance.glow * 0.3})`)
    gradient.addColorStop(0.2, 'rgba(152, 223, 207, 0.12)')
    gradient.addColorStop(1, 'rgba(76, 151, 134, 0)')
    this.context.fillStyle = gradient
    this.context.beginPath()
    this.context.arc(centerX, centerY, this.radius * 0.27, 0, Math.PI * 2)
    this.context.fill()
  }

  private drawPoint(x: number, y: number, size: number, opacity: number): void {
    this.context.fillStyle = `rgba(189, 233, 223, ${opacity})`
    this.context.beginPath()
    this.context.arc(x, y, size, 0, Math.PI * 2)
    this.context.fill()
  }

  private screenPosition(
    index: number,
    centerX: number,
    centerY: number,
    time: number,
  ): { x: number; y: number } | null {
    const node = this.simulation.nodes[index]
    const source = this.topology.nodes[index]
    if (!node || !source) {
      return null
    }
    const breathCorrection = 1 + Math.sin(time * 0.0014 + source.phase)
      * (this.appearance.breathAmplitude - 0.022)
    const irregularity = this.appearance.irregularity * 0.035
    const irregularX = Math.cos(source.phase * 2.31) * irregularity
    const irregularY = Math.sin(source.phase * 1.73) * irregularity
    return {
      x: centerX + (node.x * breathCorrection + irregularX) * this.radius,
      y: centerY + (node.y * breathCorrection + irregularY) * this.radius,
    }
  }

  private isMobile(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= 640
  }
}
